import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Users, Download, RefreshCw, CheckCircle, XCircle, 
  Clock, FileText, Brain, Target, BarChart3, AlertTriangle, 
  User, Calendar, TrendingUp, Award, Zap, ThumbsUp, ThumbsDown, HelpCircle,
  Search, X, Mail, Edit3, Send
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Loader from '../../components/ui/Loader';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import procesoService from '../../services/procesoService';
import usuarioExamenService from '../../services/usuarioExamenService';
import discService from '../../services/discService';
import ipvService from '../../services/ipvService';
import usuarioService from '../../services/usuarioService';
import perfilPruebaService from '../../services/perfilPruebaService';
import { generarReportePDF, generarReporteDISC, generarReporteCombinado } from '../../utils/pdfGenerator';
import { generarPDFIpv } from '../../utils/pdfGeneratorIpv';
import useAuthStore from '../../store/authStore';

// Colores corporativos
const COLORES = {
  naranja: '#fe7f2d',
  verde: '#297373'
};

// Helper para normalizar puntajes Raven (si vienen multiplicados por 10)
const normalizarPuntajeRaven = (puntaje, puntajeMax) => {
  let pReal = puntaje || 0;
  let pMaxReal = puntajeMax || 12;
  
  // Si el puntaje máximo es mayor a 12, viene multiplicado por 10
  if (pMaxReal > 12) {
    pReal = Math.round(pReal / 10);
    pMaxReal = Math.round(pMaxReal / 10);
  }
  
  return { puntaje: pReal, puntajeMax: pMaxReal };
};

export default function ProcesoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [proceso, setProceso] = useState(null);
  const [usuariosData, setUsuariosData] = useState([]);
  const [filtro, setFiltro] = useState('todos'); // todos, completos, pendientes
  const [busqueda, setBusqueda] = useState(''); // Buscador por DNI/nombre
  
  // Exámenes asignados al perfil del proceso (Raven, DISC, IPV)
  const [examenesAsignados, setExamenesAsignados] = useState({
    tieneRaven: false,
    tieneDisc: false,
    tieneIpv: false,
    total: 0
  });
  
  // Estado para modal de usuario
  const [modalUsuario, setModalUsuario] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [loadingUsuario, setLoadingUsuario] = useState(false);
  const [guardandoUsuario, setGuardandoUsuario] = useState(false);

  // Detectar si estamos en admin o supervisor
  const isAdmin = location.pathname.startsWith('/admin');
  const baseRoute = isAdmin ? '/admin/procesos' : '/supervisor/procesos';

  useEffect(() => {
    if (id) {
      cargarDatosProceso();
    }
  }, [id]);

  const cargarDatosProceso = async () => {
    try {
      setLoading(true);
      
      // Obtener datos del proceso
      const procesoData = await procesoService.getProcesoById(id);
      setProceso(procesoData);
      
      // Obtener los exámenes asignados a los perfiles de este proceso
      let tieneRaven = false;
      let tieneDisc = false;
      let tieneIpv = false;
      
      if (procesoData.perfiles && procesoData.perfiles.length > 0) {
        // Obtener las pruebas de cada perfil del proceso
        for (const perfil of procesoData.perfiles) {
          try {
            const pruebasPerfil = await perfilPruebaService.getPruebasByPerfil(perfil.id);
            console.log(`📋 Perfil ${perfil.nombre} - Pruebas asignadas:`, pruebasPerfil);
            
            if (pruebasPerfil && pruebasPerfil.length > 0) {
              pruebasPerfil.forEach(prueba => {
                if (prueba.examenRavenId) tieneRaven = true;
                if (prueba.testDiscId) tieneDisc = true;
                if (prueba.ipvExamenId) tieneIpv = true;
              });
            }
          } catch (err) {
            console.error(`Error obteniendo pruebas del perfil ${perfil.id}:`, err);
          }
        }
      }
      
      const totalExamenesAsignados = [tieneRaven, tieneDisc, tieneIpv].filter(Boolean).length;
      console.log('📊 Exámenes asignados al proceso:', { tieneRaven, tieneDisc, tieneIpv, total: totalExamenesAsignados });
      
      setExamenesAsignados({
        tieneRaven,
        tieneDisc,
        tieneIpv,
        total: totalExamenesAsignados
      });
      
      // Obtener usuarios del proceso
      const usuariosProceso = await procesoService.getUsuariosPorProceso(id);
      
      // Pre-cargar exámenes IPV UNA SOLA VEZ (no por cada usuario)
      let todosExamenesIpv = [];
      if (tieneIpv) {
        try {
          todosExamenesIpv = await ipvService.listarExamenes();
          console.log('📋 Exámenes IPV cargados:', todosExamenesIpv.length);
        } catch (err) {
          console.error('Error cargando exámenes IPV:', err);
        }
      }
      
      // Para cada usuario, obtener el estado de sus exámenes
      const usuariosConExamenes = await Promise.all(
        usuariosProceso.map(async (usuario) => {
          try {
            // Estado de exámenes Raven (solo si el perfil tiene Raven)
            let ravenCompletado = false;
            let ravenData = null;
            if (tieneRaven) {
              try {
                const examenesUsuario = await usuarioExamenService.obtenerExamenesUsuario(usuario.usuarioId);
                const ravenExamen = examenesUsuario.find(e => e.estado === 'FINALIZADO' || e.estado === 'COMPLETADO');
                if (ravenExamen) {
                  ravenCompletado = true;
                  ravenData = ravenExamen;
                }
              } catch (err) {
                // Sin exámenes Raven
              }
            }

            // Estado de Test DISC (solo si el perfil tiene DISC)
            let discCompletado = false;
            let discData = null;
            if (tieneDisc) {
              try {
                const testsDisc = await discService.getTestsCompletadosPorUsuario(usuario.usuarioId);
                if (testsDisc && testsDisc.length > 0) {
                  discCompletado = true;
                  discData = testsDisc[0];
                }
              } catch (err) {
                // Sin tests DISC
              }
            }

            // Estado de Examen IPV (solo si el perfil tiene IPV) - Usar datos pre-cargados
            let ipvCompletado = false;
            let ipvData = null;
            if (tieneIpv) {
              const ipvUsuario = todosExamenesIpv.find(e => e.usuarioId === usuario.usuarioId && e.estado === 'FINALIZADO');
              if (ipvUsuario) {
                ipvCompletado = true;
                ipvData = ipvUsuario;
              }
            }

            // Calcular completados y pendientes SOLO de los exámenes asignados
            const completados = [];
            const pendientes = [];
            
            if (tieneRaven) {
              if (ravenCompletado) completados.push('Raven');
              else pendientes.push('Raven');
            }
            if (tieneDisc) {
              if (discCompletado) completados.push('DISC');
              else pendientes.push('DISC');
            }
            if (tieneIpv) {
              if (ipvCompletado) completados.push('IPV');
              else pendientes.push('IPV');
            }

            return {
              ...usuario,
              examenes: {
                raven: { completado: ravenCompletado, data: ravenData, asignado: tieneRaven },
                disc: { completado: discCompletado, data: discData, asignado: tieneDisc },
                ipv: { completado: ipvCompletado, data: ipvData, asignado: tieneIpv }
              },
              totalCompletados: completados.length,
              totalAsignados: totalExamenesAsignados,
              pendientes
            };
          } catch (err) {
            return {
              ...usuario,
              examenes: {
                raven: { completado: false, data: null, asignado: tieneRaven },
                disc: { completado: false, data: null, asignado: tieneDisc },
                ipv: { completado: false, data: null, asignado: tieneIpv }
              },
              totalCompletados: 0,
              totalAsignados: totalExamenesAsignados,
              pendientes: [
                tieneRaven ? 'Raven' : null,
                tieneDisc ? 'DISC' : null,
                tieneIpv ? 'IPV' : null
              ].filter(Boolean)
            };
          }
        })
      );

      setUsuariosData(usuariosConExamenes);
    } catch (error) {
      console.error('Error cargando proceso:', error);
      toast.error('No hay usuarios asignados');
    } finally {
      setLoading(false);
    }
  };

  // Calcular métricas (solo de exámenes asignados)
  const metricas = {
    total: usuariosData.length,
    // Un usuario está "completo" si completó todos los exámenes ASIGNADOS (no siempre 3)
    completosTotal: usuariosData.filter(u => u.totalCompletados === examenesAsignados.total && examenesAsignados.total > 0).length,
    ravenCompletos: examenesAsignados.tieneRaven ? usuariosData.filter(u => u.examenes.raven.completado).length : 0,
    discCompletos: examenesAsignados.tieneDisc ? usuariosData.filter(u => u.examenes.disc.completado).length : 0,
    ipvCompletos: examenesAsignados.tieneIpv ? usuariosData.filter(u => u.examenes.ipv.completado).length : 0
  };

  // Filtrar usuarios por búsqueda y estado
  const usuariosFiltrados = usuariosData.filter(u => {
    // Filtro por estado - "completo" ahora es relativo a los exámenes asignados
    if (filtro === 'completos' && u.totalCompletados !== examenesAsignados.total) return false;
    if (filtro === 'pendientes' && u.totalCompletados >= examenesAsignados.total) return false;
    
    // Filtro por búsqueda (DNI o nombre)
    if (busqueda.trim()) {
      const termino = busqueda.toLowerCase().trim();
      const nombreMatch = u.usuarioNombre?.toLowerCase().includes(termino);
      const dniMatch = u.usuarioDni?.toLowerCase().includes(termino);
      if (!nombreMatch && !dniMatch) return false;
    }
    
    return true;
  });

  // Abrir modal de usuario
  const handleAbrirModalUsuario = async (usuario) => {
    setUsuarioSeleccionado(usuario);
    setModalUsuario(true);
    setLoadingUsuario(true);
    
    try {
      // Cargar datos completos del usuario desde el servicio
      const datosCompletos = await usuarioService.getUsuarioById(usuario.usuarioId);
      setUsuarioEditando({
        id: datosCompletos.id,
        name: datosCompletos.name || '',
        lastname: datosCompletos.lastname || '',
        dni: datosCompletos.dni || '',
        email: datosCompletos.email || '',
        rol: datosCompletos.rol || 'POSTULANTE',
        apto: datosCompletos.apto
      });
    } catch (error) {
      console.error('Error cargando usuario:', error);
      toast.error('Error al cargar datos del usuario');
      // Usar datos básicos disponibles - separar nombre si viene junto
      const nombreCompleto = usuario.usuarioNombre || '';
      const partes = nombreCompleto.split(' ');
      setUsuarioEditando({
        id: usuario.usuarioId,
        name: partes[0] || '',
        lastname: partes.slice(1).join(' ') || '',
        dni: usuario.usuarioDni || '',
        email: usuario.usuarioEmail || '',
        rol: 'POSTULANTE',
        apto: usuario.apto
      });
    } finally {
      setLoadingUsuario(false);
    }
  };

  // Cerrar modal de usuario
  const handleCerrarModalUsuario = () => {
    setModalUsuario(false);
    setUsuarioSeleccionado(null);
    setUsuarioEditando(null);
  };

  // Guardar cambios del usuario
  const handleGuardarUsuario = async () => {
    if (!usuarioEditando) return;
    
    try {
      setGuardandoUsuario(true);
      
      await usuarioService.updateUsuario(usuarioEditando.id, {
        name: usuarioEditando.name,
        lastname: usuarioEditando.lastname,
        dni: usuarioEditando.dni,
        email: usuarioEditando.email,
        rol: usuarioEditando.rol,
        apto: usuarioEditando.apto
      });
      
      // Actualizar datos locales
      const nombreCompleto = `${usuarioEditando.name} ${usuarioEditando.lastname}`.trim();
      setUsuariosData(prev => prev.map(u => 
        u.usuarioId === usuarioEditando.id 
          ? { 
              ...u, 
              usuarioNombre: nombreCompleto,
              usuarioDni: usuarioEditando.dni,
              usuarioEmail: usuarioEditando.email,
              apto: usuarioEditando.apto
            }
          : u
      ));
      
      toast.success('Usuario actualizado correctamente');
      handleCerrarModalUsuario();
    } catch (error) {
      console.error('Error actualizando usuario:', error);
      toast.error('Error al actualizar usuario');
    } finally {
      setGuardandoUsuario(false);
    }
  };

  // Reenviar credenciales
  const handleReenviarCredenciales = async () => {
    if (!usuarioEditando?.id) return;
    
    try {
      toast.loading('Enviando credenciales...', { id: 'credenciales' });
      await usuarioService.reenviarCredenciales(usuarioEditando.id);
      toast.success('Credenciales enviadas correctamente', { id: 'credenciales' });
    } catch (error) {
      console.error('Error enviando credenciales:', error);
      toast.error('Error al enviar credenciales', { id: 'credenciales' });
    }
  };

  // Descargar PDFs
  const handleDescargarRavenPDF = async (e, usuario, ravenData) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      toast.loading('Generando PDF Raven...', { id: 'pdf-raven' });
      
      const datosUsuario = {
        nombre: usuario.usuarioNombre,
        dni: usuario.usuarioDni,
        proceso: proceso?.nombre || 'Proceso de Selección',
        fechaInicio: ravenData.fechaInicio ? new Date(ravenData.fechaInicio).toLocaleString('es-PE') : 'N/A',
        fechaFin: ravenData.fechaFinalizacion ? new Date(ravenData.fechaFinalizacion).toLocaleString('es-PE') : 'N/A',
        duracion: ravenData.tiempoTotalMinutos ? `${ravenData.tiempoTotalMinutos} min` : 'N/A',
        // Fechas raw para calcular duración si es necesario
        fechaInicioRaw: ravenData.fechaInicio || null,
        fechaFinRaw: ravenData.fechaFinalizacion || null
      };
      
      const datosExamen = { nombre: 'Test de Raven - Matrices Progresivas' };
      
      // Normalizar puntajes
      const { puntaje: puntajeReal, puntajeMax: puntajeMaxReal } = normalizarPuntajeRaven(
        ravenData.puntajeObtenido, 
        ravenData.puntajeMaximo
      );
      
      const resultado = {
        respuestasCorrectas: puntajeReal,
        respuestasIncorrectas: puntajeMaxReal - puntajeReal,
        totalPreguntas: puntajeMaxReal,
        porcentajeAcierto: puntajeMaxReal ? (puntajeReal / puntajeMaxReal) * 100 : 0,
        puntosObtenidos: puntajeReal,
        nivelRendimiento: ravenData.porcentajeCompletado >= 75 ? 'NORMAL_SUPERIOR' : 
                         ravenData.porcentajeCompletado >= 50 ? 'NORMAL_PROMEDIO' : 'INFERIOR'
      };
      
      await generarReportePDF(datosUsuario, datosExamen, resultado);
      toast.success('PDF Raven generado', { id: 'pdf-raven' });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al generar PDF', { id: 'pdf-raven' });
    }
  };

  const handleDescargarDiscPDF = async (e, usuario, discData) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      toast.loading('Generando PDF DISC...', { id: 'pdf-disc' });
      await generarReporteDISC(discData, { nombre: usuario.usuarioNombre, dni: usuario.usuarioDni });
      toast.success('PDF DISC generado', { id: 'pdf-disc' });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al generar PDF', { id: 'pdf-disc' });
    }
  };

  const handleDescargarIpvPDF = async (e, usuario, ipvData) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      toast.loading('Generando PDF IPV...', { id: 'pdf-ipv' });
      
      if (ipvData.ipvEvaluacionId) {
        const resultado = await ipvService.obtenerResultadoInterpretado(ipvData.ipvEvaluacionId);
        resultado.usuarioNombre = usuario.usuarioNombre;
        resultado.nombrePostulante = usuario.usuarioNombre;
        await generarPDFIpv(resultado);
        toast.success('PDF IPV generado', { id: 'pdf-ipv' });
      } else {
        toast.error('No hay evaluación IPV disponible', { id: 'pdf-ipv' });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al generar PDF', { id: 'pdf-ipv' });
    }
  };

  // Descargar todos los PDFs de un usuario (separados)
  const handleDescargarTodosPDFs = async (e, usuario) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const { examenes } = usuario;
    let descargados = 0;

    if (examenes.raven.completado && examenes.raven.data) {
      await handleDescargarRavenPDF(null, usuario, examenes.raven.data);
      descargados++;
    }
    if (examenes.disc.completado && examenes.disc.data) {
      await handleDescargarDiscPDF(null, usuario, examenes.disc.data);
      descargados++;
    }
    if (examenes.ipv.completado && examenes.ipv.data) {
      await handleDescargarIpvPDF(null, usuario, examenes.ipv.data);
      descargados++;
    }

    if (descargados === 0) {
      toast.error('Este usuario no tiene exámenes completados');
    }
  };

  // Descargar PDF combinado (los 3 en uno)
  const handleDescargarPDFCombinado = async (e, usuario) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const { examenes } = usuario;

    if (usuario.totalCompletados === 0) {
      toast.error('Este usuario no tiene exámenes completados');
      return;
    }

    try {
      toast.loading('Generando reporte integral...', { id: 'pdf-combinado' });

      // Obtener fechas del Raven si está completado
      const ravenData = examenes.raven.data;
      
      const datosUsuario = {
        nombre: usuario.usuarioNombre,
        dni: usuario.usuarioDni,
        proceso: proceso?.nombre || 'Proceso de Selección',
        // Agregar fechas del Raven para el PDF
        fechaInicio: ravenData?.fechaInicio ? new Date(ravenData.fechaInicio).toLocaleString('es-PE') : 'N/A',
        fechaFin: ravenData?.fechaFinalizacion ? new Date(ravenData.fechaFinalizacion).toLocaleString('es-PE') : 'N/A',
        duracion: ravenData?.tiempoTotalMinutos ? `${ravenData.tiempoTotalMinutos} min` : 'N/A',
        // Fechas raw para calcular duración si es necesario
        fechaInicioRaw: ravenData?.fechaInicio || null,
        fechaFinRaw: ravenData?.fechaFinalizacion || null
      };

      // Preparar datos de Raven
      let datosRaven = null;
      if (examenes.raven.completado && examenes.raven.data) {
        const { puntaje, puntajeMax } = normalizarPuntajeRaven(
          examenes.raven.data.puntajeObtenido,
          examenes.raven.data.puntajeMaximo
        );
        const porcentaje = puntajeMax ? (puntaje / puntajeMax) * 100 : 0;
        datosRaven = {
          puntaje,
          puntajeMax,
          porcentaje,
          nivel: porcentaje >= 75 ? 'Normal Superior' : 
                 porcentaje >= 50 ? 'Normal Promedio' : 'Inferior'
        };
      }

      // Preparar datos de DISC
      let datosDisc = null;
      if (examenes.disc.completado && examenes.disc.data) {
        datosDisc = examenes.disc.data;
      }

      // Preparar datos de IPV
      let datosIpv = null;
      if (examenes.ipv.completado && examenes.ipv.data) {
        try {
          if (examenes.ipv.data.ipvEvaluacionId) {
            datosIpv = await ipvService.obtenerResultadoInterpretado(examenes.ipv.data.ipvEvaluacionId);
          }
        } catch (err) {
          console.error('Error obteniendo IPV:', err);
        }
      }

      await generarReporteCombinado(datosUsuario, datosRaven, datosDisc, datosIpv);
      toast.success('Reporte integral generado', { id: 'pdf-combinado' });

    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al generar reporte', { id: 'pdf-combinado' });
    }
  };

  // Cambiar estado de aptitud del usuario
  const handleCambiarApto = async (e, usuario, nuevoApto) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      toast.loading('Actualizando estado...', { id: 'apto-update' });
      await usuarioService.cambiarApto(usuario.usuarioId, nuevoApto);
      
      // Actualizar el estado local
      setUsuariosData(prev => prev.map(u => 
        u.usuarioId === usuario.usuarioId 
          ? { ...u, apto: nuevoApto }
          : u
      ));
      
      toast.success(`Postulante marcado como ${nuevoApto ? 'APTO' : 'NO APTO'}`, { id: 'apto-update' });
    } catch (error) {
      console.error('Error cambiando apto:', error);
      toast.error('Error al actualizar estado', { id: 'apto-update' });
    }
  };

  // Componente Badge de Aptitud
  const BadgeApto = ({ apto }) => {
    if (apto === true) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-300">
          <ThumbsUp className="w-3 h-3" />
          APTO
        </span>
      );
    }
    if (apto === false) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-300">
          <ThumbsDown className="w-3 h-3" />
          NO APTO
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-300">
        <HelpCircle className="w-3 h-3" />
        PENDIENTE
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size="lg" />
      </div>
    );
  }

  if (!proceso) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-500">Proceso no encontrado</p>
        <Button onClick={() => navigate(baseRoute)} className="mt-4">
          Volver a Procesos
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 overflow-x-hidden">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4">
        <div className="flex items-center gap-3 md:gap-4">
          <button
            onClick={() => navigate(baseRoute)}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 md:w-6 md:h-6 text-neutral-600" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-neutral-800 truncate">{proceso.nombre}</h1>
            <p className="text-sm md:text-base text-neutral-600 mt-1 line-clamp-2">{proceso.descripcion}</p>
          </div>
        </div>
        <Button variant="outline" onClick={cargarDatosProceso} className="gap-2 self-end sm:self-auto flex-shrink-0 text-sm">
          <RefreshCw className="w-4 h-4" />
          <span className="hidden sm:inline">Actualizar</span>
        </Button>
      </div>

      {/* Métricas - Grid dinámico según exámenes asignados */}
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${
        examenesAsignados.total === 3 ? 'lg:grid-cols-5' : 
        examenesAsignados.total === 2 ? 'lg:grid-cols-4' : 
        'lg:grid-cols-3'
      }`}>
        {/* Total Usuarios */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border-2 p-5 shadow-sm"
          style={{ borderColor: COLORES.verde }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500">Total Postulantes</p>
              <p className="text-3xl font-bold mt-1" style={{ color: COLORES.verde }}>{metricas.total}</p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: `${COLORES.verde}15` }}>
              <Users className="w-6 h-6" style={{ color: COLORES.verde }} />
            </div>
          </div>
        </motion.div>

        {/* Completos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border-2 border-emerald-500 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500">100% Completos</p>
              <p className="text-3xl font-bold text-emerald-600 mt-1">{metricas.completosTotal}</p>
              <p className="text-xs text-emerald-600 mt-1">
                {metricas.total > 0 ? ((metricas.completosTotal / metricas.total) * 100).toFixed(0) : 0}% del total
              </p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-xl">
              <Award className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </motion.div>

        {/* Raven - Solo si está asignado */}
        {examenesAsignados.tieneRaven && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border-2 p-5 shadow-sm"
            style={{ borderColor: COLORES.naranja }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-500">Test Raven</p>
                <p className="text-3xl font-bold mt-1" style={{ color: COLORES.naranja }}>{metricas.ravenCompletos}</p>
                <p className="text-xs mt-1" style={{ color: COLORES.naranja }}>
                  {metricas.total - metricas.ravenCompletos} pendientes
                </p>
              </div>
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${COLORES.naranja}15` }}>
                <Target className="w-6 h-6" style={{ color: COLORES.naranja }} />
              </div>
            </div>
          </motion.div>
        )}

        {/* DISC - Solo si está asignado */}
        {examenesAsignados.tieneDisc && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl border-2 border-purple-500 p-5 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-500">Test DISC</p>
                <p className="text-3xl font-bold text-purple-600 mt-1">{metricas.discCompletos}</p>
                <p className="text-xs text-purple-600 mt-1">
                  {metricas.total - metricas.discCompletos} pendientes
                </p>
              </div>
              <div className="p-3 bg-purple-50 rounded-xl">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>
        )}

        {/* IPV - Solo si está asignado */}
        {examenesAsignados.tieneIpv && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl border-2 border-indigo-500 p-5 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-500">Examen IPV</p>
                <p className="text-3xl font-bold text-indigo-600 mt-1">{metricas.ipvCompletos}</p>
                <p className="text-xs text-indigo-600 mt-1">
                  {metricas.total - metricas.ipvCompletos} pendientes
                </p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-xl">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Barra de progreso general */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-neutral-800">Progreso General del Proceso</h3>
          <span className="text-sm font-medium" style={{ color: COLORES.verde }}>
            {metricas.total > 0 ? ((metricas.completosTotal / metricas.total) * 100).toFixed(1) : 0}% completado
          </span>
        </div>
        <div className="h-4 bg-neutral-100 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${metricas.total > 0 ? (metricas.completosTotal / metricas.total) * 100 : 0}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="h-full rounded-full"
            style={{ background: `linear-gradient(90deg, ${COLORES.verde}, ${COLORES.naranja})` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-neutral-500">
          <span>{metricas.completosTotal} de {metricas.total} postulantes han completado todos los exámenes</span>
          <span>{metricas.total - metricas.completosTotal} pendientes</span>
        </div>
      </Card>

      {/* Filtros - Responsive */}
      <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFiltro('todos')}
            className={`px-3 md:px-4 py-2 rounded-lg font-medium text-xs md:text-sm transition-all ${
              filtro === 'todos' 
                ? 'text-white shadow-md' 
                : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'
            }`}
            style={filtro === 'todos' ? { backgroundColor: COLORES.verde } : {}}
          >
            Todos ({metricas.total})
          </button>
          <button
            onClick={() => setFiltro('completos')}
            className={`px-3 md:px-4 py-2 rounded-lg font-medium text-xs md:text-sm transition-all ${
              filtro === 'completos' 
                ? 'bg-emerald-500 text-white shadow-md' 
                : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'
            }`}
          >
            <CheckCircle className="w-3 h-3 md:w-4 md:h-4 inline mr-1" />
            Completos ({metricas.completosTotal})
          </button>
          <button
            onClick={() => setFiltro('pendientes')}
            className={`px-3 md:px-4 py-2 rounded-lg font-medium text-xs md:text-sm transition-all ${
              filtro === 'pendientes' 
                ? 'text-white shadow-md' 
                : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'
            }`}
            style={filtro === 'pendientes' ? { backgroundColor: COLORES.naranja } : {}}
          >
            <Clock className="w-3 h-3 md:w-4 md:h-4 inline mr-1" />
            Pendientes ({metricas.total - metricas.completosTotal})
          </button>
        </div>

        {/* Buscador por DNI/Nombre - Responsive */}
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Buscar por DNI o nombre..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="pl-9 md:pl-10 pr-10 py-2 w-full sm:w-64 md:w-72 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-[#297373] focus:border-transparent transition-all"
          />
          {busqueda && (
            <button
              onClick={() => setBusqueda('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Indicador de búsqueda activa */}
      {busqueda && (
        <div className="flex items-center gap-2 text-sm text-neutral-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
          <Search className="w-4 h-4 text-blue-500" />
          <span>
            Mostrando <strong>{usuariosFiltrados.length}</strong> resultado(s) para "<strong>{busqueda}</strong>"
          </span>
          <button
            onClick={() => setBusqueda('')}
            className="ml-auto text-blue-600 hover:text-blue-800 font-medium"
          >
            Limpiar búsqueda
          </button>
        </div>
      )}

      {/* Lista de Usuarios */}
      <div className="space-y-4">
        <AnimatePresence>
          {usuariosFiltrados.length === 0 ? (
            <Card className="p-12 text-center">
              <Users className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-500">No hay postulantes en este filtro</p>
            </Card>
          ) : (
            usuariosFiltrados.map((usuario, index) => (
              <motion.div
                key={usuario.usuarioId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className="p-0 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => handleAbrirModalUsuario(usuario)}
                >
                  <div className="p-4 md:p-5">
                    {/* Layout responsive - Stack en móvil, row en desktop */}
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      {/* Info del usuario */}
                      <div className="flex items-center gap-3 md:gap-4">
                        <div 
                          className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center text-white font-bold text-base md:text-lg shadow-md flex-shrink-0"
                          style={{ 
                            background: usuario.totalCompletados === examenesAsignados.total && examenesAsignados.total > 0
                              ? `linear-gradient(135deg, #10b981, #059669)` 
                              : `linear-gradient(135deg, ${COLORES.naranja}, ${COLORES.verde})` 
                          }}
                        >
                          {usuario.usuarioNombre?.charAt(0) || 'U'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-bold text-base md:text-lg text-neutral-800 truncate">{usuario.usuarioNombre}</h3>
                            <BadgeApto apto={usuario.apto} />
                          </div>
                          <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs md:text-sm text-neutral-500 mt-1">
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3 md:w-4 md:h-4" />
                              DNI: {usuario.usuarioDni}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                              {usuario.fechaAsignacion ? new Date(usuario.fechaAsignacion).toLocaleDateString('es-PE') : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Progreso, aptitud y acciones - Stack en móvil */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4 mt-2 lg:mt-0">
                        {/* Botones de Aptitud (solo si apto es null) */}
                        {usuario.apto === null && (
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              onClick={(e) => handleCambiarApto(e, usuario, true)}
                              className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs md:text-sm"
                            >
                              <ThumbsUp className="w-3 h-3 md:w-4 md:h-4" />
                              <span className="hidden sm:inline">APTO</span>
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={(e) => handleCambiarApto(e, usuario, false)}
                              className="gap-1 bg-red-600 hover:bg-red-700 text-white text-xs md:text-sm"
                            >
                              <ThumbsDown className="w-3 h-3 md:w-4 md:h-4" />
                              <span className="hidden sm:inline">NO APTO</span>
                            </Button>
                          </div>
                        )}

                        {/* Indicador de progreso */}
                        <div className="text-center flex-shrink-0">
                          <div className="flex items-center gap-1 justify-center">
                            {Array.from({ length: examenesAsignados.total || 1 }, (_, i) => (
                              <div
                                key={i}
                                className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full ${
                                  usuario.totalCompletados > i ? 'bg-emerald-500' : 'bg-neutral-200'
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-neutral-500 mt-1">{usuario.totalCompletados}/{examenesAsignados.total} exámenes</p>
                        </div>

                        {/* Botones de descarga - Stack en móvil */}
                        {usuario.totalCompletados > 0 && (
                          <div className="flex flex-col sm:flex-row gap-2">
                            {/* PDF Combinado (Integral) */}
                            <Button
                              type="button"
                              size="sm"
                              onClick={(e) => handleDescargarPDFCombinado(e, usuario)}
                              className="gap-1 text-xs md:text-sm whitespace-nowrap"
                              style={{ backgroundColor: COLORES.verde }}
                            >
                              <FileText className="w-3 h-3 md:w-4 md:h-4" />
                              <span className="hidden sm:inline">PAQUETE GLOBAL</span>
                              <span className="sm:hidden">GLOBAL</span>
                            </Button>
                            {/* PDFs Separados */}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={(e) => handleDescargarTodosPDFs(e, usuario)}
                              className="gap-1 text-xs md:text-sm whitespace-nowrap"
                            >
                              <Download className="w-3 h-3 md:w-4 md:h-4" />
                              <span className="hidden sm:inline">PAQUETE INDIVIDUAL</span>
                              <span className="sm:hidden">INDIVIDUAL</span>
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Estado de exámenes - Responsive grid */}
                    <div className={`grid gap-3 md:gap-4 mt-4 md:mt-5 ${
                      examenesAsignados.total === 3 ? 'grid-cols-1 sm:grid-cols-3' : 
                      examenesAsignados.total === 2 ? 'grid-cols-1 sm:grid-cols-2' : 
                      'grid-cols-1'
                    }`}>
                      {/* Raven - Solo si está asignado */}
                      {examenesAsignados.tieneRaven && (
                        <div 
                          className={`p-3 md:p-4 rounded-xl border-2 transition-all ${
                            usuario.examenes.raven.completado 
                              ? 'border-emerald-200 bg-emerald-50' 
                              : 'border-neutral-200 bg-neutral-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Target className={`w-4 h-4 md:w-5 md:h-5 ${usuario.examenes.raven.completado ? 'text-emerald-600' : 'text-neutral-400'}`} />
                              <span className="font-semibold text-sm md:text-base text-neutral-800">Raven</span>
                            </div>
                            {usuario.examenes.raven.completado ? (
                              <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-emerald-500" />
                            ) : (
                              <XCircle className="w-4 h-4 md:w-5 md:h-5 text-neutral-300" />
                            )}
                          </div>
                          {usuario.examenes.raven.completado ? (
                            <>
                              <p className="text-xs md:text-sm text-emerald-700 mb-2">
                                Puntaje: {(() => {
                                  const { puntaje, puntajeMax } = normalizarPuntajeRaven(
                                    usuario.examenes.raven.data?.puntajeObtenido,
                                    usuario.examenes.raven.data?.puntajeMaximo
                                  );
                                  return `${puntaje}/${puntajeMax}`;
                                })()}
                              </p>
                              <button
                                type="button"
                                onClick={(e) => handleDescargarRavenPDF(e, usuario, usuario.examenes.raven.data)}
                                className="w-full py-1.5 md:py-2 px-3 text-xs md:text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1"
                                style={{ backgroundColor: COLORES.naranja, color: 'white' }}
                              >
                                <Download className="w-3 h-3 md:w-4 md:h-4" /> PDF
                              </button>
                            </>
                          ) : (
                            <p className="text-xs md:text-sm text-neutral-500 flex items-center gap-1">
                              <Clock className="w-3 h-3 md:w-4 md:h-4" /> Pendiente
                            </p>
                          )}
                        </div>
                      )}

                      {/* DISC - Solo si está asignado */}
                      {examenesAsignados.tieneDisc && (
                        <div 
                          className={`p-3 md:p-4 rounded-xl border-2 transition-all ${
                            usuario.examenes.disc.completado 
                              ? 'border-purple-200 bg-purple-50' 
                              : 'border-neutral-200 bg-neutral-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Brain className={`w-4 h-4 md:w-5 md:h-5 ${usuario.examenes.disc.completado ? 'text-purple-600' : 'text-neutral-400'}`} />
                              <span className="font-semibold text-sm md:text-base text-neutral-800">DISC</span>
                            </div>
                            {usuario.examenes.disc.completado ? (
                              <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-purple-500" />
                            ) : (
                              <XCircle className="w-4 h-4 md:w-5 md:h-5 text-neutral-300" />
                            )}
                          </div>
                          {usuario.examenes.disc.completado ? (
                            <>
                              <p className="text-xs md:text-sm text-purple-700 mb-2">
                                Perfil: {usuario.examenes.disc.data?.perfilResultado || 'N/A'}
                              </p>
                              <button
                                type="button"
                                onClick={(e) => handleDescargarDiscPDF(e, usuario, usuario.examenes.disc.data)}
                                className="w-full py-1.5 md:py-2 px-3 bg-purple-600 text-white text-xs md:text-sm font-medium rounded-lg hover:bg-purple-700 transition-all flex items-center justify-center gap-1"
                              >
                                <Download className="w-3 h-3 md:w-4 md:h-4" /> PDF
                              </button>
                            </>
                          ) : (
                            <p className="text-xs md:text-sm text-neutral-500 flex items-center gap-1">
                              <Clock className="w-3 h-3 md:w-4 md:h-4" /> Pendiente
                            </p>
                          )}
                        </div>
                      )}

                      {/* IPV - Solo si está asignado */}
                      {examenesAsignados.tieneIpv && (
                        <div 
                          className={`p-3 md:p-4 rounded-xl border-2 transition-all ${
                            usuario.examenes.ipv.completado 
                              ? 'border-indigo-200 bg-indigo-50' 
                              : 'border-neutral-200 bg-neutral-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <TrendingUp className={`w-4 h-4 md:w-5 md:h-5 ${usuario.examenes.ipv.completado ? 'text-indigo-600' : 'text-neutral-400'}`} />
                              <span className="font-semibold text-sm md:text-base text-neutral-800">IPV</span>
                            </div>
                            {usuario.examenes.ipv.completado ? (
                              <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-indigo-500" />
                            ) : (
                              <XCircle className="w-4 h-4 md:w-5 md:h-5 text-neutral-300" />
                            )}
                          </div>
                          {usuario.examenes.ipv.completado ? (
                            <>
                              <p className="text-xs md:text-sm text-indigo-700 mb-2">
                                Completado ✓
                              </p>
                              <button
                                type="button"
                                onClick={(e) => handleDescargarIpvPDF(e, usuario, usuario.examenes.ipv.data)}
                                className="w-full py-1.5 md:py-2 px-3 bg-indigo-600 text-white text-xs md:text-sm font-medium rounded-lg hover:bg-indigo-700 transition-all flex items-center justify-center gap-1"
                              >
                                <Download className="w-3 h-3 md:w-4 md:h-4" /> PDF
                              </button>
                            </>
                          ) : (
                            <p className="text-xs md:text-sm text-neutral-500 flex items-center gap-1">
                              <Clock className="w-3 h-3 md:w-4 md:h-4" /> Pendiente
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Alerta de pendientes */}
                    {usuario.pendientes.length > 0 && (
                      <div 
                        className="mt-4 p-3 rounded-lg flex items-center gap-2"
                        style={{ backgroundColor: `${COLORES.naranja}15` }}
                      >
                        <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: COLORES.naranja }} />
                        <p className="text-sm" style={{ color: COLORES.naranja }}>
                          <span className="font-medium">Pendiente:</span> {usuario.pendientes.join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Modal de Edición de Usuario */}
      <Modal
        isOpen={modalUsuario}
        onClose={handleCerrarModalUsuario}
        title="Detalle del Postulante"
        size="lg"
      >
        {loadingUsuario ? (
          <div className="flex items-center justify-center py-12">
            <Loader size="lg" />
          </div>
        ) : usuarioEditando ? (
          <div className="space-y-3">
            {/* Header del modal con avatar - Compacto */}
            <div className="flex items-center gap-3 pb-3 border-b border-neutral-200">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0"
                style={{ 
                  background: `linear-gradient(135deg, ${COLORES.naranja}, ${COLORES.verde})` 
                }}
              >
                {usuarioEditando.name?.charAt(0) || 'U'}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-sm text-neutral-800 truncate">{usuarioEditando.name} {usuarioEditando.lastname}</h3>
                <p className="text-xs text-neutral-500">DNI: {usuarioEditando.dni}</p>
              </div>
            </div>

            {/* Formulario de edición - Compacto */}
            <div className="grid grid-cols-1 gap-2">
              {/* Nombre */}
              <div>
                <label className="block text-[10px] font-medium text-neutral-600 mb-0.5">
                  Nombres
                </label>
                <input
                  type="text"
                  value={usuarioEditando.name}
                  onChange={(e) => setUsuarioEditando(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-neutral-300 rounded-lg focus:ring-1 focus:ring-[#297373] focus:border-transparent"
                />
              </div>

              {/* Apellidos */}
              <div>
                <label className="block text-[10px] font-medium text-neutral-600 mb-0.5">
                  Apellidos
                </label>
                <input
                  type="text"
                  value={usuarioEditando.lastname}
                  onChange={(e) => setUsuarioEditando(prev => ({ ...prev, lastname: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-neutral-300 rounded-lg focus:ring-1 focus:ring-[#297373] focus:border-transparent"
                />
              </div>

              {/* DNI y Email en fila */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-neutral-600 mb-0.5">
                    DNI
                  </label>
                  <input
                    type="text"
                    value={usuarioEditando.dni}
                    onChange={(e) => setUsuarioEditando(prev => ({ ...prev, dni: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm border border-neutral-300 rounded-lg focus:ring-1 focus:ring-[#297373] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-neutral-600 mb-0.5">
                    Correo
                  </label>
                  <input
                    type="email"
                    value={usuarioEditando.email}
                    onChange={(e) => setUsuarioEditando(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm border border-neutral-300 rounded-lg focus:ring-1 focus:ring-[#297373] focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Estado de Aptitud - Responsive */}
            <div>
              <label className="block text-xs md:text-sm font-medium text-neutral-700 mb-2">
                Estado de Aptitud
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setUsuarioEditando(prev => ({ ...prev, apto: true }))}
                  className={`py-2 px-2 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-xs ${
                    usuarioEditando.apto === true
                      ? 'bg-emerald-500 text-white shadow-md'
                      : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                  }`}
                >
                  <ThumbsUp className="w-4 h-4" />
                  <span>APTO</span>
                </button>
                <button
                  type="button"
                  onClick={() => setUsuarioEditando(prev => ({ ...prev, apto: false }))}
                  className={`py-2 px-2 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-xs ${
                    usuarioEditando.apto === false
                      ? 'bg-red-500 text-white shadow-md'
                      : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                  }`}
                >
                  <ThumbsDown className="w-4 h-4" />
                  <span>NO APTO</span>
                </button>
                <button
                  type="button"
                  onClick={() => setUsuarioEditando(prev => ({ ...prev, apto: null }))}
                  className={`py-2 px-2 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-xs col-span-2 sm:col-span-1 ${
                    usuarioEditando.apto === null
                      ? 'bg-amber-500 text-white shadow-md'
                      : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                  }`}
                >
                  <HelpCircle className="w-4 h-4" />
                  <span>PENDIENTE</span>
                </button>
              </div>
            </div>

            {/* Información de exámenes - Responsive */}
            {usuarioSeleccionado && (
              <div className="bg-neutral-50 rounded-xl p-3 border border-neutral-200">
                <h4 className="font-semibold text-xs md:text-sm text-neutral-800 mb-2">Estado de Exámenes</h4>
                <div className="grid grid-cols-3 gap-2">
                  {examenesAsignados.tieneRaven && (
                    <div className={`p-2 rounded-lg text-center ${usuarioSeleccionado.examenes?.raven?.completado ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-500'}`}>
                      <Target className="w-4 h-4 mx-auto mb-0.5" />
                      <span className="text-[10px] font-medium block">Raven</span>
                      <p className="text-[9px]">{usuarioSeleccionado.examenes?.raven?.completado ? '✓' : 'Pend.'}</p>
                    </div>
                  )}
                  {examenesAsignados.tieneDisc && (
                    <div className={`p-2 rounded-lg text-center ${usuarioSeleccionado.examenes?.disc?.completado ? 'bg-purple-100 text-purple-700' : 'bg-neutral-200 text-neutral-500'}`}>
                      <Brain className="w-4 h-4 mx-auto mb-0.5" />
                      <span className="text-[10px] font-medium block">DISC</span>
                      <p className="text-[9px]">{usuarioSeleccionado.examenes?.disc?.completado ? '✓' : 'Pend.'}</p>
                    </div>
                  )}
                  {examenesAsignados.tieneIpv && (
                    <div className={`p-2 rounded-lg text-center ${usuarioSeleccionado.examenes?.ipv?.completado ? 'bg-indigo-100 text-indigo-700' : 'bg-neutral-200 text-neutral-500'}`}>
                      <TrendingUp className="w-4 h-4 mx-auto mb-0.5" />
                      <span className="text-[10px] font-medium block">IPV</span>
                      <p className="text-[9px]">{usuarioSeleccionado.examenes?.ipv?.completado ? '✓' : 'Pend.'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Botones de acción - Responsive */}
            <div className="flex flex-col gap-2 pt-3 border-t border-neutral-200">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCerrarModalUsuario}
                  className="flex-1 text-xs py-2"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleGuardarUsuario}
                  disabled={guardandoUsuario}
                  style={{ backgroundColor: COLORES.verde }}
                  className="flex-1 gap-1 text-xs py-2"
                >
                  {guardandoUsuario ? (
                    <Loader size="sm" />
                  ) : (
                    <>
                      <Edit3 className="w-3 h-3" />
                      <span>Guardar</span>
                    </>
                  )}
                </Button>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleReenviarCredenciales}
                className="gap-1 text-xs py-2 w-full"
              >
                <Send className="w-3 h-3" />
                <span>Reenviar Credenciales</span>
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
